import React, { useCallback, useEffect, useMemo } from "react";
import { Redirect, useParams } from "react-router-dom";

import Alert from "veneer/Alert";

import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  applicationsSelector,
  installedAppsSelector,
} from "cortex/ui/src/redux-store/applications";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { EDIT_DEVICE_PARAMS } from "cortex/ui/src/redux-store/devices";
import { deviceStoresLoadedSelector } from "cortex/ui/src/redux-store/devices/selectors";
import {
  addErrorMessageAction,
  addSuccessMessageAction,
} from "cortex/ui/src/redux-store/messages";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import {
  currentDeviceUnloadAction,
  fetchDeviceAction,
} from "cortex/ui/src/redux-store/ui-state/current-device/actions";
import { currentDeviceSelector } from "cortex/ui/src/redux-store/ui-state/current-device/selectors";
import { isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import { SimpleFetch } from "cortex/ui/src/ui/fetch";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import { Container } from "cortex/ui/src/view/layout/Container";

import ConfigureDeviceParams from "./ConfigureDeviceParams";
import { Device } from "./model";

const getAllAppsAsArray = makeGetAllAsArray();

export default function ConfigureDevicePage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const dispatch = useAppDispatch();
  const device = useAppSelector(currentDeviceSelector);
  const devicesStoresLoaded = useAppSelector(deviceStoresLoadedSelector);
  const appById = useAppSelector(applicationsSelector);
  const apps = useMemo(() => getAllAppsAsArray({ byId: appById }), [appById]);
  const installedApps = useMemo(() => installedAppsSelector(apps), [apps]);
  const networksById = useAppSelector(networksSelector);
  const networksStoresLoaded = useAppSelector(networksStoresLoadedSelector);
  const user = useAppSelector(getAuthUser);
  const readonly = isReadonly(user);
  const storesLoaded = useMemo(
    () => devicesStoresLoaded && networksStoresLoaded,
    [devicesStoresLoaded, networksStoresLoaded],
  );

  useEffect(() => {
    if (!deviceId) {
      return;
    }

    dispatch(
      fetchDeviceAction({
        serviceArgs: { deviceId },
      }),
    );

    return () => {
      dispatch(currentDeviceUnloadAction);
    };
  }, [dispatch, deviceId]);

  const network = device?.network_id
    ? (networksById[device.network_id] ?? null)
    : null;

  const addErrorMessage = useCallback(
    (msg: string) => {
      dispatch(addErrorMessageAction(msg));
    },
    [dispatch],
  );

  const addSuccessMessage = useCallback(
    (msg: string) => {
      dispatch(addSuccessMessageAction(msg));
    },
    [dispatch],
  );

  const updateParams = useCallback(
    (serviceArgs: { id: number; params: Device["user_params"] }) => {
      return createPromiseApiAction(dispatch, EDIT_DEVICE_PARAMS, [
        serviceArgs.id,
        serviceArgs.params,
      ]).then(() => {
        addSuccessMessage("Device parameters updated successfully.");
      });
    },
    [addSuccessMessage, dispatch],
  );

  if (!deviceId) {
    return <Redirect to="/404" />;
  }

  if (!storesLoaded || !device) {
    return (
      <Container p={1} title="">
        <NavLoader.Proxy />
      </Container>
    );
  }

  if (device && device?.device_id !== deviceId) {
    return <Redirect to="/404" />;
  }

  const title = `Configure ${device.name}`;
  const breadcrumbs = getBreadcrumbs([
    { label: "Devices", target: "/devices" },
    { label: device.name, target: `/devices/${device.device_id}` },
  ]);

  return (
    <Container breadcrumbs={breadcrumbs} hideBox p={1} title={title}>
      <SimpleFetch.Network
        errorChild={
          <Alert severity="error">Could not fetch network for device.</Alert>
        }
        injectAs="network"
        networkId={device.network_id!}
      >
        <ConfigureDeviceParams
          addErrorMessage={addErrorMessage}
          apps={installedApps}
          device={device}
          network={network}
          readonly={readonly}
          updateParams={updateParams}
        />
      </SimpleFetch.Network>
    </Container>
  );
}
